import React, { useState, useEffect, useCallback } from 'react';
import Box from '@mui/material/Box';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import CircularProgress from '@mui/material/CircularProgress';
import Alert from '@mui/material/Alert';
import Grid from '@mui/material/Grid';
import TextField from '@mui/material/TextField';
import Button from '@mui/material/Button';
import LinearProgress from '@mui/material/LinearProgress';
import { ReportService } from '../../services/reportService';
import { QACoverageReportData } from '../../types/QACoverageReport';
import { ReportExportButton } from './ReportExportButton';

export const QACoverageReport: React.FC = () => {
  const [report, setReport] = useState<QACoverageReportData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Default to current month
  const [startDate, setStartDate] = useState(() => {
    const d = new Date();
    return new Date(d.getFullYear(), d.getMonth(), 1).toISOString().split('T')[0];
  });
  const [endDate, setEndDate] = useState(() => new Date().toISOString().split('T')[0]);

  const fetchReport = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await ReportService.getQACoverageReport(startDate, endDate);
      setReport(data);
    } catch {
      setError('Failed to load QA coverage report.');
    } finally {
      setLoading(false);
    }
  }, [startDate, endDate]);

  useEffect(() => {
    fetchReport();
  }, [fetchReport]);

  // Rendered above every branch — loading, error and empty included. A period that fails to load
  // or returns nothing is exactly when a user needs to change the period, and returning a bare
  // Alert leaves them with no control but to leave the tab and come back.
  const dateFilter = (
    <Paper sx={{ p: 2 }}>
      <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
        <TextField
          label="Start Date"
          type="date"
          size="small"
          value={startDate}
          onChange={(e) => setStartDate(e.target.value)}
          slotProps={{ inputLabel: { shrink: true } }}
        />
        <TextField
          label="End Date"
          type="date"
          size="small"
          value={endDate}
          onChange={(e) => setEndDate(e.target.value)}
          slotProps={{ inputLabel: { shrink: true } }}
        />
        <Button variant="contained" size="small" onClick={fetchReport}>
          Update
        </Button>
        <ReportExportButton
          report="qa-coverage"
          label="QA Coverage"
          startDate={startDate}
          endDate={endDate}
          busy={loading}
        />
        <Typography variant="caption" color="text.secondary" sx={{ width: '100%' }}>
          Dates are calendar days in the agency&apos;s timezone.
        </Typography>
      </Box>
    </Paper>
  );

  if (loading) {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
        {dateFilter}
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
          <CircularProgress aria-label="Loading QA coverage report" />
        </Box>
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
        {dateFilter}
        <Alert severity="error">{error}</Alert>
      </Box>
    );
  }

  if (!report || report.totalCallsIngested === 0) {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
        {dateFilter}
        <Alert severity="info">
          No calls found for the selected period. An export of this period will contain the report
          headings and no data rows.
        </Alert>
      </Box>
    );
  }

  const aiAnalyzedPct = report.totalCallsIngested > 0
    ? Math.round((report.aiAnalyzedCount / report.totalCallsIngested) * 100)
    : 0;
  const autoAuditedPct = report.aiAnalyzedCount > 0
    ? Math.round((report.autoAuditedCount / report.aiAnalyzedCount) * 100)
    : 0;
  const flaggedPct = report.aiAnalyzedCount > 0
    ? Math.round((report.flaggedForReviewCount / report.aiAnalyzedCount) * 100)
    : 0;
  const supervisorAuditedPct = report.flaggedForReviewCount > 0
    ? Math.round((report.supervisorAuditedCount / report.flaggedForReviewCount) * 100)
    : 0;

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
      {dateFilter}

      {/* Summary Cards */}
      <Grid container spacing={2}>
        <Grid size={{ xs: 12, sm: 6, md: 2 }}>
          <Paper sx={{ p: 2, textAlign: 'center' }}>
            <Typography variant="body2" color="text.secondary">Total Ingested</Typography>
            <Typography variant="h4">{report.totalCallsIngested}</Typography>
          </Paper>
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 2 }}>
          <Paper sx={{ p: 2, textAlign: 'center' }}>
            <Typography variant="body2" color="text.secondary">AI Analyzed ✅</Typography>
            <Typography variant="h4">{report.aiAnalyzedCount}</Typography>
            <Typography variant="caption" color="text.secondary">{aiAnalyzedPct}%</Typography>
          </Paper>
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 2 }}>
          <Paper sx={{ p: 2, textAlign: 'center' }}>
            <Typography variant="body2" color="text.secondary">Auto-Audited</Typography>
            <Typography variant="h4">{report.autoAuditedCount}</Typography>
            <Typography variant="caption" color="text.secondary">{autoAuditedPct}%</Typography>
          </Paper>
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 2 }}>
          <Paper sx={{ p: 2, textAlign: 'center' }}>
            <Typography variant="body2" color="text.secondary">Flagged for Review</Typography>
            <Typography variant="h4">{report.flaggedForReviewCount}</Typography>
            <Typography variant="caption" color="text.secondary">{flaggedPct}%</Typography>
          </Paper>
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 2 }}>
          <Paper sx={{ p: 2, textAlign: 'center' }}>
            <Typography variant="body2" color="text.secondary">Supervisor Audited</Typography>
            <Typography variant="h4">{report.supervisorAuditedCount}</Typography>
            <Typography variant="caption" color="text.secondary">{supervisorAuditedPct}% of flagged</Typography>
          </Paper>
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 2 }}>
          <Paper sx={{ p: 2, textAlign: 'center' }}>
            <Typography variant="body2" color="text.secondary">
              Pending Review {report.pendingReviewCount > 0 ? '⚠️' : ''}
            </Typography>
            <Typography variant="h4" color={report.pendingReviewCount > 0 ? 'warning.main' : 'text.primary'}>
              {report.pendingReviewCount}
            </Typography>
          </Paper>
        </Grid>
      </Grid>

      {/* Review Turnaround */}
      <Paper sx={{ p: 2 }}>
        <Typography variant="subtitle1" gutterBottom>Review Turnaround</Typography>
        <Grid container spacing={3}>
          <Grid size={{ xs: 12, sm: 6 }}>
            <Typography variant="body2" color="text.secondary">Average Review Time</Typography>
            <Typography variant="h5">
              {report.avgReviewTurnaroundDays} days
            </Typography>
          </Grid>
          <Grid size={{ xs: 12, sm: 6 }}>
            <Typography variant="body2" color="text.secondary">Oldest Pending Review</Typography>
            <Typography
              variant="h5"
              color={report.oldestPendingReviewDays != null && report.oldestPendingReviewDays > 7 ? 'error.main' : 'text.primary'}
            >
              {report.oldestPendingReviewDays != null
                ? `${report.oldestPendingReviewDays} days ${report.oldestPendingReviewDays > 7 ? '🔴' : ''}`
                : 'None pending'}
            </Typography>
          </Grid>
        </Grid>
      </Paper>

      {/* Per-Operator Coverage */}
      {report.operatorCoverage.length > 0 && (
        <Paper sx={{ p: 2 }}>
          <Typography variant="subtitle1" gutterBottom>Per-Operator Coverage</Typography>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Operator</TableCell>
                  <TableCell align="right">Total Calls</TableCell>
                  <TableCell align="right">Audited</TableCell>
                  <TableCell align="right">Coverage %</TableCell>
                  <TableCell sx={{ minWidth: 150 }}>Progress</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {report.operatorCoverage.map((op) => (
                  <TableRow key={op.operatorId}>
                    <TableCell>{op.operatorName}</TableCell>
                    <TableCell align="right">{op.totalCalls}</TableCell>
                    <TableCell align="right">{op.auditedCalls}</TableCell>
                    <TableCell align="right">{op.coveragePercentage}%</TableCell>
                    <TableCell>
                      <LinearProgress
                        variant="determinate"
                        value={op.coveragePercentage}
                        sx={{ height: 10, borderRadius: 1 }}
                        color={op.coveragePercentage >= 80 ? 'success' : op.coveragePercentage >= 50 ? 'warning' : 'error'}
                      />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      )}

    </Box>
  );
};
